import type { OverviewKpiSeries, UserKpiEntry } from "@nexus/contracts";
import { formatUserKpiValue } from "@/features/kpi-settings/lib/user-kpi-model";
import type { UserKpiCellValue } from "@/features/kpi-settings/components/UserKpiGrid";
import { SHARE_OF_TOTAL_KPIS, resolveKpiBadge } from "./topicDetailKpis";

/**
 * Per-KPI display values for the Topic Detail grid, keyed by `UserKpiEntry.name`.
 *
 * The server formats `formatted` / `avgFormatted` from the ORG format, decimals and
 * direction, so a user who overrode any of those would otherwise read a stale
 * string. Values are re-derived from the raw `value` and `series.avg` through the
 * user's own settings; the server string is kept only as the null-value fallback.
 */
export function buildTopicDetailKpiCells(
  entries: UserKpiEntry[],
  kpiSeries: OverviewKpiSeries[],
  topicTitle: string,
  options: { currencySymbol?: string; emptyValue?: string } = {},
): Record<string, UserKpiCellValue> {
  const { currencySymbol = "$", emptyValue = "—" } = options;
  const seriesByKpi = new Map(kpiSeries.map((s) => [s.kpi, s]));
  const cells: Record<string, UserKpiCellValue> = {};

  for (const entry of entries) {
    const series = seriesByKpi.get(entry.name);
    if (!series) continue;

    const point = series.data.find((p) => p.Topic_Title === topicTitle);
    if (!point) {
      cells[entry.name] = { value: emptyValue, badgeText: null, tone: "neutral" };
      continue;
    }

    // Badge arithmetic runs against the user's direction and precision so the
    // tone matches what they see, not what the org configured.
    const effectiveSeries = { ...series, polarity: entry.polarity, format: entry.format };
    const badge = resolveKpiBadge(entry.name, point, effectiveSeries, entry.decimals);

    cells[entry.name] = {
      value: resolveMainValue(entry, series, point, currencySymbol, emptyValue),
      badgeText: badge.badgeText,
      tone: badge.tone,
    };
  }

  return cells;
}

function resolveMainValue(
  entry: UserKpiEntry,
  series: OverviewKpiSeries,
  point: OverviewKpiSeries["data"][number],
  currencySymbol: string,
  emptyValue: string,
): string {
  if (point.value === null || point.value === undefined) {
    return point.formatted || emptyValue;
  }

  const value = formatUserKpiValue(point.value, entry.format, entry.decimals, currencySymbol, emptyValue);
  if (!SHARE_OF_TOTAL_KPIS.has(entry.name)) return value;

  // Share-of-total KPIs read "mine/total"; the denominator must be re-derived too
  // or the two halves would disagree on precision.
  if (series.avg === null || series.avg === undefined) return value;
  const total = formatUserKpiValue(series.avg, entry.format, entry.decimals, currencySymbol, emptyValue);
  return `${value}/${total}`;
}
